import {
    RESOURCE_TYPE_HELPFUL_WEBSITE,
    RESOURCE_TYPE_OTHER_RESOURCES,
    RESOURCE_TYPE_SAMPLE_ASSESSMENTS
} from "@/ts/constants";
import { StudentAssignmentStatusEnum } from "@/ts/enums/StudentAssignmentStatusEnum";
import {
    AssignmentAssestInterface,
    AssignmentDeliverableInterface,
    AssignmentDetailInterface,
    AssignmentInterface,
    AssignmentResourcesInterface,
    GradeInterface,
    HelpfulResourcesInterface,
    MarkedAssignmentInterface,
    MarkerAssessmentAssestInterface,
    OverviewInterface,
    SubmissionInterface
} from "@/ts/interfaces/MarkingPortal/AssignmentInterface";
import {
    AssignmentApiInterface,
    AssignmentAssestApiInterface,
    AssignmentDeliverableApiInterface,
    AssignmentExemptionApiInterface,
    AssignmentResourcesApiInterface,
    GradeApiInterface,
    MarkerAssessmentAssestApiInterface,
    PreviousSubmissionsApiInterface,
    SubmissionApiInterface
} from "@/ts/interfaces/MarkingPortal/api/AssignmentApiInterface";
import { UserInterface } from "@/ts/interfaces/Parent/UserInterface";

import {
    getFileUploaderFileTypes,
    isNonAssessedByGrade
} from "./markingPortalHelper";

const ASSEST_TYPE_MARKED_FILE = "marked_file";
const ASSEST_TYPE_MARKING_RUBRIC = "marking_rubric";
const ASSEST_TYPE_DISPLAY_IMAGE = "display_image";
const ASSIGNMENT_ASSEST_FILE = "assignment_file";
const ASSIGNMENT_ASSEST__RUBRIC = "assignment_rubric";

const mapAssignmentDetailApiData = (
    assignment: AssignmentApiInterface,
    assignmentAssets: AssignmentAssestApiInterface[],
    assignmentResources: AssignmentResourcesApiInterface[],
    assignmentDeliverables: AssignmentDeliverableApiInterface[],
    previousSubmissions: PreviousSubmissionsApiInterface[],
    hasExceededAllowedExemptions: boolean,
    exemptions: AssignmentExemptionApiInterface[]
): AssignmentDetailInterface | undefined => {
    if (!assignment) return undefined;

    const displayImageAssets = mapAssetsByType(
        assignmentAssets,
        ASSEST_TYPE_DISPLAY_IMAGE
    );
    const assignmentAssetFiles = mapAssetsByType(
        assignmentAssets,
        ASSIGNMENT_ASSEST_FILE
    );
    const assignemtnAssetRubricFiles = mapAssetsByType(
        assignmentAssets,
        ASSIGNMENT_ASSEST__RUBRIC
    );

    const overview: OverviewInterface = {
        description: assignment.description,
        whatToSubmit: assignmentDeliverables
            ? assignmentDeliverables.map(
                (
                    deliverable: AssignmentDeliverableApiInterface
                ): AssignmentDeliverableInterface => ({
                    uuid: deliverable.uuid,
                    allowLink: deliverable.allow_link,
                    allowUpload: deliverable.allow_upload,
                    description: deliverable.description,
                    mandatory: deliverable.mandatory,
                    fileTypes: getFileUploaderFileTypes(deliverable.file_type)
                })
            )
            : [],
        purpose: {
            description: assignment.purpose ?? "",
            image:
                displayImageAssets.length > 0
                    ? displayImageAssets[0]
                    : undefined
        },
        learningOutcome: assignment.learning_outcome || [],
        downloadAssignment:
            assignmentAssetFiles.length > 0 ? assignmentAssetFiles[0] : null,
        downloadMarkingRubric:
            assignemtnAssetRubricFiles.length > 0
                ? assignemtnAssetRubricFiles[0]
                : null
    };

    const helpfulResources: HelpfulResourcesInterface = assignmentResources
        ? {
            sampleAssessments: assignmentResources
                .filter(
                    resource =>
                        resource.type === RESOURCE_TYPE_SAMPLE_ASSESSMENTS
                )
                .map(mapStudentAssignmentResourcesApiData),
            helpfulWebsites: assignmentResources
                .filter(
                    resource =>
                        resource.type === RESOURCE_TYPE_HELPFUL_WEBSITE
                )
                .map(mapStudentAssignmentResourcesApiData),
            otherResources: assignmentResources
                .filter(
                    resource =>
                        resource.type === RESOURCE_TYPE_OTHER_RESOURCES
                )
                .map(mapStudentAssignmentResourcesApiData)
        }
        : {
            sampleAssessments: [],
            helpfulWebsites: [],
            otherResources: []
        };

    const markedSubmissions: MarkedAssignmentInterface[] = previousSubmissions
        ? previousSubmissions.map(
            (previousSubmission: PreviousSubmissionsApiInterface) => {
                const marks = parseFloat(previousSubmission.marks ?? "0");
                const maxMarks = parseFloat(
                    previousSubmission.max_mark || "100"
                );

                const percentage =
                    maxMarks > 0 ? Math.round((marks / maxMarks) * 100) : 0;

                const rejectedReasons = previousSubmission.reject_reasons;
                const isNotMarked = rejectedReasons?.length > 0;
                const displayMarks = !isNotMarked
                    ? `${marks} out of ${maxMarks}`
                    : "-";

                const displayPercentage = !isNotMarked
                    ? `${percentage}%`
                    : "-";

                return {
                    id: previousSubmission.uuid,
                    name: previousSubmission.assignment_name,
                    subject: previousSubmission.subject,
                    submittedOn: previousSubmission.submitted_on,
                    feedback: previousSubmission.overall_comment,
                    marks: marks.toString(),
                    displayMarks,
                    status: mapStatus(previousSubmission.status),
                    maxMark: maxMarks.toString(),
                    percentage,
                    displayPercentage,
                    isNotMarked,
                    rejectedReasons,
                    submittedAssignments:
                        previousSubmission.student_submission_asset
                            ?.map(mapMarkerAssessmentAssestApi)
                            .filter(
                                (
                                    item
                                ): item is MarkerAssessmentAssestInterface =>
                                    item !== undefined
                            ) ?? [],
                    markedAssignments:
                        previousSubmission.marker_assessment_asset
                            .filter(
                                asset =>
                                    asset.type === ASSEST_TYPE_MARKED_FILE
                            )
                            .map(mapMarkerAssessmentAssestApi)
                            .filter(
                                (
                                    asset
                                ): asset is MarkerAssessmentAssestInterface =>
                                    asset !== undefined
                            ),
                    markedRubic: previousSubmission.marker_assessment_asset
                        .filter(
                            asset => asset.type === ASSEST_TYPE_MARKING_RUBRIC
                        )
                        .map(mapMarkerAssessmentAssestApi)
                        .filter(
                            (
                                asset
                            ): asset is MarkerAssessmentAssestInterface =>
                                asset !== undefined
                        )
                };
            }
        )
        : [];

    const highestPercentage = Math.max(
        ...markedSubmissions.map(submission => submission.percentage)
    );
    const updatedMarkedSubmissions: MarkedAssignmentInterface[] =
        markedSubmissions.map(submission => ({
            ...submission,
            isHighest:
                !submission.isNotMarked &&
                submission.percentage === highestPercentage
        }));

    return {
        overview,
        helpfulResources,
        markedSubmissions: updatedMarkedSubmissions,
        hasExceededAllowedExemptions,
        exemptions
    };
};

const getStatusWithNonAssessedState = (
    assignment: AssignmentApiInterface,
    isNonAssessed: boolean
) => {
    if (isNonAssessed) {
        return StudentAssignmentStatusEnum.MARKS_NOT_AVAILABLE;
    }

    return assignment?.latest_submission
        ? mapStatus(assignment?.latest_submission?.status)
        : mapStatus("to-do");
};

export const mapStudentAssignmentApiData = (
    assignment: AssignmentApiInterface,
    isNonAssessed: boolean
): AssignmentInterface => {
    const marks = Number(assignment?.highest_marks);
    const maxMark = Number(assignment.max_mark);
    const percentage =
        !isNaN(marks) && !isNaN(maxMark) && maxMark > 0
            ? Math.round((marks / maxMark) * 100)
            : 0;

    const data: AssignmentInterface = {
        uuid: assignment.uuid,
        name: assignment.name,
        subject: assignment.subject,
        term: assignment.term?.toString(),
        startWeek: assignment.start_week,
        submissionWeek: assignment.submission_week,
        marks: assignment?.highest_marks ?? "",
        maxMark: assignment.max_mark,
        percentage: percentage.toString(),
        status: getStatusWithNonAssessedState(assignment, isNonAssessed),
        grade: mapGradeApiToGrade(assignment.grade),
        productFamilyId: assignment.grade.id.toString(),
        latestSubmission: assignment?.latest_submission
            ? mapLatestSubmission(assignment?.latest_submission)
            : null,
        studentId: undefined
    };
    return data;
};

export const mapStudentAssignmentWithDetailsApiData = (
    assignment: AssignmentApiInterface,
    assignmentAssets: AssignmentAssestApiInterface[],
    assignmentResources: AssignmentResourcesApiInterface[],
    assignmentDeliverables: AssignmentDeliverableApiInterface[],
    previousSubmissions: PreviousSubmissionsApiInterface[],
    hasExceededAllowedExemptions: boolean,
    exemptions: AssignmentExemptionApiInterface[],
    isNonAssessed: boolean
): AssignmentInterface => {
    const marks = Number(assignment?.latest_submission?.marks);
    const maxMark = Number(assignment.max_mark);
    const percentage =
        !isNaN(marks) && !isNaN(maxMark) && maxMark > 0
            ? Math.round((marks / maxMark) * 100)
            : 0;

    const data: AssignmentInterface = {
        uuid: assignment.uuid,
        name: assignment.name,
        subject: assignment.subject,
        term: assignment.term?.toString(),
        studentId: undefined,
        startWeek: assignment.start_week,
        submissionWeek: assignment.submission_week,
        marks: marks.toString(),
        maxMark: maxMark.toString(),
        percentage: percentage.toString(),
        status: getStatusWithNonAssessedState(assignment, isNonAssessed),
        grade: mapGradeApiToGrade(assignment.grade),
        productFamilyId: assignment.grade.id.toString(),
        assignmentDetail: mapAssignmentDetailApiData(
            assignment,
            assignmentAssets,
            assignmentResources,
            assignmentDeliverables,
            previousSubmissions,
            hasExceededAllowedExemptions,
            exemptions
        ),
        latestSubmission: assignment?.latest_submission
            ? mapLatestSubmission(assignment?.latest_submission)
            : null
    };
    return data;
};

export const mapLatestSubmission = (
    data: SubmissionApiInterface
): SubmissionInterface => {
    return {
        uuid: data.uuid,
        submittedOn: data.submitted_on,
        marks: data.marks,
        type: data.type,
        status: data.status,
        maxMark: data.max_mark,
        overallComment: data.overall_comment,
        assignmentName: data.assignment_name,
        subject: data.subject,
        rejectReasons: data.reject_reasons,
        viewedAt: data.viewed_at,
        markerAssessmentAsset:
            data.marker_assessment_asset
                ?.map(mapMarkerAssessmentAssestApi)
                .filter(
                    (item): item is MarkerAssessmentAssestInterface =>
                        item !== undefined
                ) || [],
        studentSubmissionAsset: mapStudentSubmissionAsset(data)
    };
};

export const mapStudentSubmissionAsset = (data: SubmissionApiInterface) => {
    if (data.status === "marked" || data.status === "resubmission required") {
        return [];
    }

    return (
        data.student_submission_asset
            ?.map(mapMarkerAssessmentAssestApi)
            .filter(
                (item): item is MarkerAssessmentAssestInterface =>
                    item !== undefined
            ) || []
    );
};

export const mapStudentAssignmentListApiData = (
    assignments: AssignmentApiInterface[],
    auth: UserInterface
): AssignmentInterface[] => {
    return assignments.map(assignment => {
        const isNonAssessed = isNonAssessedByGrade(
            assignment.grade.grade_code.toString(),
            auth
        );
        return mapStudentAssignmentApiData(assignment, isNonAssessed);
    });
};

export const mapStudentAssignmentResourcesApiData = (
    resource: AssignmentResourcesApiInterface
): AssignmentResourcesInterface => {
    return {
        downloadLink: resource.download_link,
        downloadable: resource.downloadable,
        fileName: resource.file_name,
        fileType: resource.file_type,
        size: resource.size,
        type: resource.type,
        uuid: resource.uuid,
        websiteLink: resource.website_link ? resource.website_link : ""
    };
};

export const mapAssetsByType = (
    assets: AssignmentAssestApiInterface[],
    type: string
): AssignmentAssestInterface[] => {
    if (!assets) {
        return [];
    }

    return assets
        .filter(asset => asset.type === type)
        .map(asset => ({
            id: asset.id,
            downloadLink: asset.download_link,
            fileName: asset.file_name,
            fileType: asset.file_type,
            size: asset.size,
            type: asset.type
        }));
};

export const mapMarkerAssessmentAssestApi = (
    asset?: MarkerAssessmentAssestApiInterface
): MarkerAssessmentAssestInterface | undefined => {
    return asset
        ? ({
            uuid: asset.uuid,
            id: asset.asset_id,
            disk: asset.disk,
            path: asset.path,
            name: asset.name,
            downloadLink: asset.download_link,
            type: asset.type,
            deliverableId: asset?.assignment_deliverable?.uuid
        } as MarkerAssessmentAssestInterface)
        : undefined;
};

export const mapStatus = (status: string): StudentAssignmentStatusEnum => {
    switch (status) {
        case "to-do":
        case "in progress":
            return StudentAssignmentStatusEnum.TODO;
        case "pending":
        case "pending marks":
            return StudentAssignmentStatusEnum.PENDING;
        case "marked":
        case "approved":
            return StudentAssignmentStatusEnum.MARKED;
        case "marksNotAvailable":
            return StudentAssignmentStatusEnum.MARKS_NOT_AVAILABLE;
        case "exempted":
            return StudentAssignmentStatusEnum.EXEMPTED;
        case "resubmission required":
            return StudentAssignmentStatusEnum.RESUBMISSION_REQUIRED;
        default:
            throw new Error(`Unknown status: ${status}`);
    }
};

export const mapGradeApiToGrade = (
    resource: GradeApiInterface
): GradeInterface => {
    return {
        id: resource.id,
        countryId: resource.country_id,
        productFamilyId: resource.product_family_id,
        gradeCode: resource.grade_code,
        grade: resource.grade,
        nextGrade: resource.next_grade,
        upgradeAllowed: resource.upgrade_allowed,
        shortName: resource.short_name,
        longName: resource.long_name,
        status: resource.status,
        createdAt: resource.created_at,
        updatedAt: resource.updated_at,
        deletedAt: resource.deleted_at,
        year: resource.year
    };
};
